// Permission helper constants and utilities.
//
// Usage in component templates (via GiganautVue base class helpers):
//   <v-btn v-if="canManage(Permissions.Workers)">New Worker</v-btn>
//   <v-list-item v-if="canView(Permissions.Bookings)">Bookings</v-list-item>
//
// Usage in TypeScript code:
//   import { Permissions, hasPermission, canManage, canView } from '@/lib/permissions';
//   if (canManage(Permissions.SettingsRoles)) { ... }

import { AppModule } from '@/store/modules/app';

// Permission levels - must match backend api/core/agency_roles.go
export const PERMISSION_NONE = 0;
export const PERMISSION_VIEW = 1;
export const PERMISSION_MANAGE = 2;

// Permission code string constants - must match backend PERMISSION_CODE_* values.
// Exposed as an object so templates can reference them like `Permissions.Workers`.
export const Permissions = {
    // Feature permissions
    Workers: 'WORKERS',
    Companies: 'COMPANIES',
    Locations: 'LOCATIONS',
    Contacts: 'CONTACTS',
    Bookings: 'BOOKINGS',
    Jobs: 'JOBS',
    Opportunities: 'OPPORTUNITIES',
    Payroll: 'PAYROLL',
    SpecOuts: 'SPEC_OUTS',
    Invoices: 'INVOICES',
    Timesheets: 'TIMESHEETS',
    Campaigns: 'CAMPAIGNS',
    Messaging: 'MESSAGING',
    Reports: 'REPORTS',
    Activities: 'ACTIVITIES',
    Compliance: 'COMPLIANCE',
    Kpis: 'KPIS',
    // Settings - Agency section
    SettingsAgencyProfile: 'SETTINGS_AGENCY_PROFILE',
    SettingsBranches: 'SETTINGS_BRANCHES',
    SettingsRateCards: 'SETTINGS_RATE_CARDS',
    SettingsUniforms: 'SETTINGS_UNIFORMS',
    SettingsReportingPeriods: 'SETTINGS_REPORTING_PERIODS',
    SettingsKpis: 'SETTINGS_KPIS',
    // Settings - Users & Permissions section
    SettingsUsers: 'SETTINGS_USERS',
    SettingsGroups: 'SETTINGS_GROUPS',
    SettingsRoles: 'SETTINGS_ROLES',
    // Settings - Communication section
    SettingsEmailTemplates: 'SETTINGS_EMAIL_TEMPLATES',
    SettingsConversationTopics: 'SETTINGS_CONVERSATION_TOPICS',
    SettingsBusinessHours: 'SETTINGS_BUSINESS_HOURS',
    // Settings - Categories section
    SettingsNoteCategories: 'SETTINGS_NOTE_CATEGORIES',
    SettingsActivityCategories: 'SETTINGS_ACTIVITY_CATEGORIES',
    // Settings - Work Types section
    SettingsIndustries: 'SETTINGS_INDUSTRIES',
    SettingsLocationTypes: 'SETTINGS_LOCATION_TYPES',
    SettingsSkills: 'SETTINGS_SKILLS',
    SettingsQualifications: 'SETTINGS_QUALIFICATIONS',
    SettingsPositions: 'SETTINGS_POSITIONS',
    // Settings - Integrations section
    SettingsIntegrations: 'SETTINGS_INTEGRATIONS',
    SettingsMobileApp: 'SETTINGS_MOBILE_APP',
    // Settings - Sales / Pipeline section
    SettingsOpportunitySources: 'SETTINGS_OPPORTUNITY_SOURCES',
    SettingsOpportunityLostReasons: 'SETTINGS_OPPORTUNITY_LOST_REASONS',
    // Settings - Other
    SettingsFaq: 'SETTINGS_FAQ',
} as const;

export type PermissionCode = typeof Permissions[keyof typeof Permissions];

/**
 * Returns true if the current user has at least `minLevel` for the given permission code.
 * Administrators always return true. If there is no user profile loaded, returns false.
 */
export function hasPermission(code: PermissionCode, minLevel: number): boolean {
    const profile = AppModule.userProfile;
    if (!profile) {
        return false;
    }
    if (profile.isAdministrator) {
        return true;
    }
    const level = profile.permissions ? profile.permissions[code] : 0;
    return (level || 0) >= minLevel;
}

/** Returns true if the user can view (level >= VIEW) the given area. */
export function canView(code: PermissionCode): boolean {
    return hasPermission(code, PERMISSION_VIEW);
}

/** Returns true if the user can manage (level >= MANAGE) the given area. */
export function canManage(code: PermissionCode): boolean {
    return hasPermission(code, PERMISSION_MANAGE);
}

// All granular settings permission codes - used for gating the Settings entry point.
const SETTINGS_PERMISSION_CODES: PermissionCode[] = [
    Permissions.SettingsAgencyProfile,
    Permissions.SettingsBranches,
    Permissions.SettingsRateCards,
    Permissions.SettingsUniforms,
    Permissions.SettingsReportingPeriods,
    Permissions.SettingsKpis,
    Permissions.SettingsUsers,
    Permissions.SettingsGroups,
    Permissions.SettingsRoles,
    Permissions.SettingsEmailTemplates,
    Permissions.SettingsConversationTopics,
    Permissions.SettingsBusinessHours,
    Permissions.SettingsNoteCategories,
    Permissions.SettingsActivityCategories,
    Permissions.SettingsIndustries,
    Permissions.SettingsLocationTypes,
    Permissions.SettingsSkills,
    Permissions.SettingsQualifications,
    Permissions.SettingsPositions,
    Permissions.SettingsIntegrations,
    Permissions.SettingsMobileApp,
    Permissions.SettingsOpportunitySources,
    Permissions.SettingsOpportunityLostReasons,
    Permissions.SettingsFaq,
];

/** Returns true if the user has at least VIEW on any settings permission (used to gate the Settings menu). */
export function hasAnySettingsPermission(): boolean {
    return SETTINGS_PERMISSION_CODES.some(code => canView(code));
}
